import { ReportDataResponse, ReportResult } from "@/api/auth/report";
import { roundNumber } from "@/utils/round";
import mapKnownValues from "@/utils/mapKnownValues";
import sortBy from "lodash/sortBy";

export class ReportingMetricResult {
  metricValue = 0;
  end = new Date();
  start = new Date();

  constructor(params: Partial<ReportingMetricResult>) {
    mapKnownValues(this, params);
  }
}

export class ReportingChargeSessions extends ReportingMetricResult {
  constructor(params: Partial<ReportingMetricResult>) {
    super(params);
    this.metricValue = roundNumber(params.metricValue);
  }
}

export class ReportingEnergyConsumedKwh extends ReportingMetricResult {
  constructor(params: Partial<ReportingMetricResult>) {
    super(params);
    this.metricValue = roundNumber(params.metricValue, 1);
  }
}

export class ReportingEnergyDemandKwh extends ReportingMetricResult {
  constructor(params: Partial<ReportingMetricResult>) {
    super(params);
    this.metricValue = roundNumber(params.metricValue, 1);
  }
}

// On the chart, it should be represented as a "%" and 0-100
export class ReportingEnergyUtilizationKwh extends ReportingMetricResult {
  constructor(params: Partial<ReportingMetricResult>) {
    super(params);
    const value = params.metricValue === undefined ? 0 : params.metricValue * 100;
    this.metricValue = roundNumber(value, 0);
  }
}

export class ReportingRevenue extends ReportingMetricResult {
  constructor(params: Partial<ReportingMetricResult>) {
    super(params);
    this.metricValue = params.metricValue ?? 0;
  }
}

export class ReportingUniqueDrivers extends ReportingMetricResult {
  constructor(params: Partial<ReportingMetricResult>) {
    super(params);
    this.metricValue = roundNumber(params.metricValue);
  }
}

export default class ReportingMetrics {
  chargeSessions!: ReportingMetricResult[];
  energyConsumedKwh!: ReportingMetricResult[];
  energyDemandKwh!: ReportingMetricResult[];
  energyUtilizationKwh!: ReportingMetricResult[];
  revenue!: ReportingMetricResult[];
  uniqueDrivers!: ReportingMetricResult[];

  constructor(params: Partial<ReportingMetrics>) {
    mapKnownValues(this, params);
    this.chargeSessions = sortBy(params.chargeSessions || [], d => d.end).map(
      data => new ReportingChargeSessions(data)
    );
    this.energyConsumedKwh = sortBy(params.energyConsumedKwh || [], d => d.end).map(
      data => new ReportingEnergyConsumedKwh(data)
    );
    this.energyDemandKwh = sortBy(params.energyDemandKwh || [], d => d.end).map(
      data => new ReportingEnergyDemandKwh(data)
    );
    this.energyUtilizationKwh = sortBy(params.energyUtilizationKwh || [], d => d.end).map(
      data => new ReportingEnergyUtilizationKwh(data)
    );
    this.revenue = sortBy(params.revenue || [], d => d.end).map(data => new ReportingRevenue(data));
    this.uniqueDrivers = sortBy(params.uniqueDrivers || [], d => d.end).map(data => new ReportingUniqueDrivers(data));
  }

  static fromAPI(response: ReportDataResponse) {
    function formatAPIResultData(data: ReportResult) {
      return new ReportingMetricResult({
        metricValue: data.metricValue,
        start: data.start ? new Date(data.start) : undefined,
        end: data.end ? new Date(data.end) : undefined
      });
    }
    return new ReportingMetrics({
      chargeSessions: (response.chargeSessions || []).map(formatAPIResultData),
      energyConsumedKwh: (response.energyConsumedKwh || []).map(formatAPIResultData),
      energyDemandKwh: (response.energyDemandKwh || []).map(formatAPIResultData),
      energyUtilizationKwh: (response.energyUtilizationKwh || []).map(formatAPIResultData),
      revenue: (response.revenue || []).map(formatAPIResultData),
      uniqueDrivers: (response.uniqueDrivers || []).map(formatAPIResultData)
    });
  }
}
